import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useUpdateDocument } from "@/hooks/documents/useDocumentMutations";
import type { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

/**
 * Publishing a branded document to the Genie Page is exactly one thing:
 * flipping `is_public` on the document row. The public help-hub handler already
 * lists every attached, public, active document as a guide. New documents are
 * published automatically on create (see useSaveGenieDocument) — this hook is
 * the owner's toggle to take one off the page, or put it back.
 */
export const usePublishGenieDocument = () => {
  const updateDocument = useUpdateDocument();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const setPublished = useCallback(
    async (document: DocumentWithMetadata, published: boolean) => {
      setPendingId(document.id);
      try {
        await updateDocument.mutateAsync({ document, updates: { is_public: published } });
        toast.success(
          published ? "Published to your Genie Page" : "Removed from your Genie Page",
        );
      } catch {
        // useUpdateDocument already toasts the failure.
      } finally {
        setPendingId(null);
      }
    },
    [updateDocument],
  );

  return { setPublished, pendingId };
};
